import {
  CODE_CERTHASH,
  CODE_IP4,
  CODE_IP6,
  CODE_P2P,
  CODE_P2P_CIRCUIT,
  CODE_UDP,
  CODE_WS,
  multiaddr,
  type Multiaddr,
} from '@multiformats/multiaddr';

const CODE_WEBTRANSPORT = 0x01d1;

type MultiaddrComponent = ReturnType<Multiaddr['getComponents']>[number];

function findComponentValue(
  components: MultiaddrComponent[],
  code: number,
): string | undefined {
  return components.find((component) => component.code === code)?.value;
}

function hasComponent(components: MultiaddrComponent[], code: number): boolean {
  return components.some((component) => component.code === code);
}

function parseIpv4(host: string): number[] | null {
  const octets = host.split('.').map(Number);
  if (
    octets.length !== 4 ||
    octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)
  ) {
    return null;
  }
  return octets;
}

function normalizeIpv6(host: string): string {
  return host.trim().toLowerCase();
}

function isLoopbackIpv4(host: string): boolean {
  const octets = parseIpv4(host);
  return octets ? octets[0] === 127 : false;
}

function isUnspecifiedIpv4(host: string): boolean {
  return host === '0.0.0.0';
}

function isPrivateIpv4(host: string): boolean {
  const octets = parseIpv4(host);
  if (!octets) return false;
  const [first, second] = octets;
  return (
    first === 10 ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168) ||
    (first === 169 && second === 254)
  );
}

function isLoopbackIpv6(host: string): boolean {
  return normalizeIpv6(host) === '::1';
}

function isUnspecifiedIpv6(host: string): boolean {
  return normalizeIpv6(host) === '::';
}

function isPrivateIpv6(host: string): boolean {
  const normalized = normalizeIpv6(host);
  return (
    normalized.startsWith('fc') ||
    normalized.startsWith('fd') ||
    normalized.startsWith('fe80:')
  );
}

function hasValidUdpPort(components: MultiaddrComponent[]): boolean {
  const portValue = findComponentValue(components, CODE_UDP);
  if (!portValue) return false;
  const port = Number(portValue);
  return Number.isInteger(port) && port > 0 && port <= 65535;
}

export function isLoopbackOrUnspecifiedMultiaddr(addr: Multiaddr): boolean {
  const components = addr.getComponents();
  const ip4 = findComponentValue(components, CODE_IP4);
  if (ip4) {
    return isLoopbackIpv4(ip4) || isUnspecifiedIpv4(ip4);
  }

  const ip6 = findComponentValue(components, CODE_IP6);
  if (ip6) {
    return isLoopbackIpv6(ip6) || isUnspecifiedIpv6(ip6);
  }

  return false;
}

export function isPrivateMultiaddr(addr: Multiaddr): boolean {
  const components = addr.getComponents();
  const ip4 = findComponentValue(components, CODE_IP4);
  if (ip4) {
    return isPrivateIpv4(ip4) || isLoopbackIpv4(ip4) || isUnspecifiedIpv4(ip4);
  }

  const ip6 = findComponentValue(components, CODE_IP6);
  if (ip6) {
    return isPrivateIpv6(ip6) || isLoopbackIpv6(ip6) || isUnspecifiedIpv6(ip6);
  }

  return false;
}

export function isScopedSidecarDialMultiaddr(addr: Multiaddr): boolean {
  const components = addr.getComponents();

  if (hasComponent(components, CODE_P2P_CIRCUIT)) {
    return true;
  }

  if (hasComponent(components, CODE_WEBTRANSPORT)) {
    return (
      hasComponent(components, CODE_CERTHASH) && hasValidUdpPort(components)
    );
  }

  return false;
}

export function shouldDenyBrowserDialMultiaddr(addr: Multiaddr): boolean {
  const components = addr.getComponents();

  // Keep the browser default of denying insecure ws dials.
  if (hasComponent(components, CODE_WS)) {
    return true;
  }

  if (!isPrivateMultiaddr(addr)) {
    return false;
  }

  if (isLoopbackOrUnspecifiedMultiaddr(addr)) {
    return true;
  }

  return !isScopedSidecarDialMultiaddr(addr);
}

export function sanitizeSidecarDialMultiaddrs(
  rawMultiaddrs: string[],
): Multiaddr[] {
  const unique = new Set<string>();
  const sanitized: Multiaddr[] = [];

  for (const rawAddr of rawMultiaddrs) {
    let parsed: Multiaddr;
    try {
      parsed = multiaddr(rawAddr);
    } catch {
      continue;
    }

    if (isLoopbackOrUnspecifiedMultiaddr(parsed)) {
      continue;
    }

    const canonical = parsed.toString();
    if (unique.has(canonical)) {
      continue;
    }
    unique.add(canonical);
    sanitized.push(parsed);
  }

  return sanitized;
}

export function buildRelayCircuitMultiaddrs(
  relayMultiaddrs: string[],
  targetPeerId: string,
): string[] {
  const unique = new Set<string>();
  const dialableRelayAddrs: string[] = [];

  for (const relayRaw of relayMultiaddrs) {
    let relayAddr: Multiaddr;
    try {
      relayAddr = multiaddr(relayRaw);
    } catch {
      continue;
    }

    const relayComponents = relayAddr.getComponents();
    if (!hasComponent(relayComponents, CODE_P2P)) {
      continue;
    }

    const base = relayAddr.toString();
    const withCircuit = hasComponent(relayComponents, CODE_P2P_CIRCUIT)
      ? base
      : `${base}/p2p-circuit`;
    const candidateRaw = withCircuit.endsWith(`/p2p/${targetPeerId}`)
      ? withCircuit
      : `${withCircuit}/p2p/${targetPeerId}`;

    let candidate: Multiaddr;
    try {
      candidate = multiaddr(candidateRaw);
    } catch {
      continue;
    }

    const canonical = candidate.toString();
    if (unique.has(canonical)) {
      continue;
    }
    unique.add(canonical);
    dialableRelayAddrs.push(canonical);
  }

  return dialableRelayAddrs;
}
